import React, { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Carousel, Modal } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { CarouselRef } from 'antd/lib/carousel';
import { EntranceImage, PackImage } from 'apollo/types';
import placeholderImage from 'assets/images/placeholder-image.png';
import useMaxBreakpoint from 'utils/styles/useMaxBreakpoint';
import { Breakpoints } from 'utils/styles/breakpoints';
import styles from './ImageGallery.module.scss';

const VISIBLE_IMAGES = 3;

interface ImageGalleryProps {
  images: (PackImage | EntranceImage)[];
  name: string;
}

const ImageGallery: FC<ImageGalleryProps> = ({ images, name }: ImageGalleryProps) => {
  const { t } = useTranslation();
  const carousel = useRef<CarouselRef>(null);
  const [isOpen, setIsOpen] = useState(false);

  const maxBreakpoint = useMaxBreakpoint();
  const isMobile = Number.isFinite(maxBreakpoint) && maxBreakpoint < Breakpoints.MD;

  const sources = images.map((image) => image.image || placeholderImage);
  const visible = Array.from(
    { length: VISIBLE_IMAGES },
    (unused, index) => sources[index] || placeholderImage
  );
  const extraImages = sources.length - VISIBLE_IMAGES;

  const openAt = (index: number) => {
    setIsOpen(true);
    // The carousel only exists once the modal has rendered.
    window.setTimeout(() => carousel.current?.goTo(index, true), 0);
  };

  const renderImage = (index: number, className: string) => {
    const isOverlayTile = index === VISIBLE_IMAGES - 1 && extraImages > 0;
    return (
      <button
        type="button"
        className={`${styles.Tile} ${className}`}
        onClick={() => openAt(index)}
        aria-label={t('pages.ticketInformation.openGallery')}
      >
        <img className={styles.Image} src={visible[index]} alt={name} />
        {isOverlayTile && (
          <span className={styles.Overlay}>
            <span className={styles.OverlayCount}>{`+${extraImages}`}</span>
          </span>
        )}
      </button>
    );
  };

  if (isMobile) {
    return (
      <Carousel className={styles.MobileCarousel} dots>
        {sources.map((source) => (
          <div key={source}>
            <div className={styles.SquareSlide}>
              <img className={styles.SquareImage} src={source} alt={name} />
            </div>
          </div>
        ))}
      </Carousel>
    );
  }

  return (
    <>
      <div className={styles.Gallery}>
        {renderImage(0, styles.MainTile)}
        <div className={styles.SideColumn}>
          {renderImage(1, styles.SideTile)}
          {renderImage(2, styles.SideTile)}
        </div>
      </div>
      <Modal
        className={styles.GalleryModal}
        visible={isOpen}
        footer={null}
        closable={false}
        centered
        width="90vw"
        onCancel={() => setIsOpen(false)}
      >
        <div className={styles.CarouselWrapper}>
          {sources.length > 1 && (
            <button
              type="button"
              className={`${styles.Arrow} ${styles.ArrowPrev}`}
              onClick={() => carousel.current?.prev()}
              aria-label={t('pages.ticketInformation.previousImage')}
            >
              <LeftOutlined />
            </button>
          )}
          <Carousel ref={carousel} dots>
            {sources.map((source) => (
              <div key={source}>
                {/* Closes only when the empty space beside the image is clicked,
                    never the image itself. */}
                <div
                  className={styles.Slide}
                  role="presentation"
                  onClick={(event) => {
                    if (event.target === event.currentTarget) setIsOpen(false);
                  }}
                >
                  <img className={styles.CarouselImage} src={source} alt={name} />
                </div>
              </div>
            ))}
          </Carousel>
          {sources.length > 1 && (
            <button
              type="button"
              className={`${styles.Arrow} ${styles.ArrowNext}`}
              onClick={() => carousel.current?.next()}
              aria-label={t('pages.ticketInformation.nextImage')}
            >
              <RightOutlined />
            </button>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ImageGallery;
